/**
 * Utility functions for formatting data.
 */
import { formatDistanceToNow, isBefore, subMonths } from "date-fns";
import { format, toZonedTime } from "date-fns-tz";
import dayjs from "dayjs";
import { replace } from "lodash";
import numeral from "numeral";
import { v5 as uuidv5 } from "uuid";

/**
 * Splits a string at the specified index.
 * @param str - The input string.
 * @param index - The index at which to split the string.
 * @returns An array containing the two parts of the split string.
 */
function split(str: string, index: number): string[] {
  const result = [str.slice(0, index), str.slice(index)];
  return result;
}

/**
 * Generates a unique key for a list using UUID v5.
 * @param input - The input value.
 * @returns A unique key for the list item.
 */
function getListItemKey(input): string {
  return uuidv5(String(input), uuidv5.URL);
}

/**
 * Converts a rem value to pixels.
 * @param value - The rem value to convert.
 * @returns The converted value in pixels.
 */
function remToPx(value: string): number {
  return Math.round(parseFloat(value) * 16);
}

/**
 * Converts a pixel value to rem.
 * @param value - The pixel value to convert.
 * @returns The converted value in rem.
 */
function pxToRem(value: number): string {
  return `${value / 16}rem`;
}

/**
 * Formats a number as currency.
 * @param number - The number to format.
 * @param nullIfZero - Whether to return null if the number is zero (optional).
 * @param currency - The currency it is in, given where that is not its
 * business's: it says which - "US$48.91", "€20", "¥7,500" - where an amount in
 * the business's own reads "$48.91".
 * @returns The formatted currency string.
 */
export function fCurrency(
  number: number | string,
  nullIfZero?: boolean,
  alwaysShowDecimals = false,
  currency?: string | null,
): string {
  // If number is a string, check that it is not 0
  if (typeof number === "string" && parseFloat(number) === 0 && nullIfZero) {
    return null;
  }
  const numericValue = Number(number);
  let formattedCurrency = numeral(number).format(
    alwaysShowDecimals || !Number.isInteger(numericValue) ? "$0,0.00" : "$0,0",
  );

  if (
    (typeof number === "string" && parseFloat(number) < 0) ||
    (typeof number === "number" && number < 0)
  ) {
    formattedCurrency = `(${formattedCurrency.slice(1)})`;
  }
  if (currency) {
    // Its own sign, as a Canadian reads US dollars ("US$") and an American
    // reads every other ("CA$", "€", "¥") - or, one no one has a sign for
    // (what a statement or a spreadsheet's row says), what it was given.
    let symbol = currency;
    try {
      symbol =
        new Intl.NumberFormat(currency === "USD" ? "en-CA" : "en-US", {
          style: "currency",
          currency,
        })
          .formatToParts(0)
          .find((part) => part.type === "currency")?.value ?? currency;
    } catch {
      // Not a currency Intl knows.
    }
    formattedCurrency = formattedCurrency.replace("$", symbol);
  }
  return formattedCurrency;
}

/**
 * Formats a number as a percentage.
 * @param number - The number to format.
 * @returns The formatted percentage string.
 */
function fPercent(number: number): string {
  return numeral(number / 100).format("0.0%");
}

/**
 * Formats a number.
 * @param number - The number to format.
 * @returns The formatted number string.
 */
function fNumber(number: number): string {
  if (number === 0) {
    return "0";
  }
  return numeral(number).format("0,0.00");
}

/**
 * Formats a number and shortens it.
 * @param number - The number to format.
 * @returns The formatted and shortened number string.
 */
function fShortenNumber(number: number): string {
  return replace(numeral(number).format("0.00a"), ".00", "");
}

/**
 * Formats a number with a specified precision.
 * @param number - The number to format.
 * @param precision - The number of decimal places to include.
 * @returns The formatted number string with the specified precision.
 */
function fPrecision(number: number, precision = 0): number {
  return Number(Number(number).toFixed(precision)) / 1; // divide by 1 to trim trailing zeros
}

/**
 * Formats a number as data.
 * @param number - The number to format.
 * @returns The formatted data string.
 */
function fData(number: number): string {
  return numeral(number).format("0.0 b");
}

/**
 * Gets the precision of a number.
 * @param number - The number to get the precision of.
 * @returns The precision of the number.
 */
function getPrecision(number: number): number {
  const index = String(number).indexOf(".") + 1;
  return number && index > 0 ? String(number).length - index : 0;
}

/**
 * Formats a date.
 * @param date - The date to format.
 * @returns The formatted date string.
 */
function fDate(date: Date): string {
  // Set timezone to UTC
  const timeZone = "UTC";
  date = toZonedTime(date, timeZone);

  try {
    const dateStr = format(date, "MMMM dd, yy", { timeZone });
    // Split the month from the day and year,
    // and shorten the month to 3 characters
    const splitDate = split(dateStr, dateStr.indexOf(" ") + 1);
    return splitDate[0].slice(0, 3) + " " + splitDate[1] + "'";
  } catch {
    return "";
  }
}

/**
 * Formats a date for a project.
 * @param date - The date to format.
 * @returns The formatted date string.
 */
function fProjectDate(date: Date): string {
  return format(date, "MMM yyyy");
}

/**
 * Formats a date with the month and year in the local timezone.
 * @param date - The date to format.
 * @param timeZone - The timezone to use (optional).
 * @returns The formatted date string.
 */
function fLocalMonthYear(
  date: Date,
  timeZone: string | null = null,
): string | null {
  return date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        ...(timeZone ? { timeZone } : {}),
      })
    : null;
}

/**
 * Formats a date with the month, day, and weekday in the local timezone.
 * @param date - The date to format.
 * @param timeZone - The timezone to use (optional).
 * @returns The formatted date string.
 */
function fLocalMonthDay(date: Date, timeZone: string | null = null): string {
  if (!date) {
    return "";
  }

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    weekday: "short",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Formats a date with the month in the local timezone.
 * @param date - The date to format.
 * @param timeZone - The timezone to use (optional).
 * @returns The formatted date string.
 */
function fLocalMonth(
  date: Date,
  timeZone: string | null = null,
): string | null {
  return date
    ? new Date(date).toLocaleDateString("en-US", {
        month: "long",
        ...(timeZone ? { timeZone } : {}),
      })
    : null;
}

/**
 * Formats a date with the day of the week in the local timezone.
 * @param date - The date to format.
 * @returns The formatted date string.
 */
function fLocalDay(date: Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
  });
}

/**
 * Formats a date with the month, day, and year in the local timezone.
 * @param date - The date to format.
 * @param timeZone - The timezone to use (optional).
 * @returns The formatted date string.
 */
function fLocalDate(date: Date, timeZone: string | null = null): string | null {
  return date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit",
        ...(timeZone ? { timeZone } : {}),
      })
    : null;
}

/**
 * Formats a date with the month, day, and year in the local timezone (short format).
 * @param date - The date to format.
 * @returns The formatted date string.
 */
function fLocalDateShort(date: Date): string | null {
  return date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : null;
}

/**
 * Formats a time.
 * @param date - The date to format.
 * @returns The formatted time string.
 */
function fTime(date: Date): string {
  return format(date, "p");
}

/**
 * Formats a time in the local timezone.
 * @param date - The date to format.
 * @returns The formatted time string.
 */
function fLocalTime(date: Date): string {
  return new Date(date).toLocaleTimeString("en-US");
}

/**
 * Formats a date and time.
 * @param date - The date to format.
 * @returns The formatted date and time string.
 */
function fDateTime(date: Date): string {
  return format(date, "dd MMM yyyy HH:mm");
}

/**
 * Formats a date and time with AM/PM suffix.
 * @param date - The date to format.
 * @returns The formatted date and time string.
 */
function fDateTimeSuffix(date: Date): string {
  return format(date, "dd/MM/yyyy hh:mm p");
}

/**
 * Formats a date and time (short format).
 * @param date - The date to format.
 * @returns The formatted date and time string.
 */
function fDateTimeShort(date: Date): string {
  return format(date, "dd/MM/yyyy");
}

/**
 * Formats a date relative to the current time.
 * @param date - The date to format.
 * @returns The formatted relative date string.
 */
function fToNow(date: Date): string {
  return formatDistanceToNow(date, {
    addSuffix: true,
  });
}

/**
 * Formats a date relative to now, falling back to an absolute datetime once
 * the date is more than two months in the past.
 */
function fToNowOrDateTime(date: Date): string {
  return fLastSyncedAt(date).label;
}

function fLastSyncedAt(date: Date): { label: string; isRelative: boolean } {
  const isRelative = !isBefore(date, subMonths(new Date(), 2));
  return {
    label: isRelative ? fToNow(date) : fDateTime(date),
    isRelative,
  };
}

/**
 * Formats a date into a short time ago string (e.g. "3m ago", "2h ago")
 * @param date - The date or string to format
 * @returns The formatted time ago string
 */
function fTimeAgo(date: Date | string): string {
  if (!date) {
    return "";
  }

  if (typeof date === "string") {
    date = new Date(date);
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 30) {
    return `${diffDays}d ago`;
  } else {
    return fLocalDateShort(date);
  }
}

/**
 * Gets the last day of the month for a given year and month.
 * @param year - The year.
 * @param month - The month (0-11).
 * @returns The last day of the month.
 */
function getLastDayOfMonth(year: number, month: number): number {
  const date = new Date(year, month + 1, 0);
  return date.getDate();
}

/**
 * Gets the number of days in a month for a given year and month.
 * @param year - The year.
 * @param monthNumber - The month (0-11).
 * @returns The number of days in the month.
 */
function daysInMonth(year: number, monthNumber: number): number {
  return new Date(year, monthNumber, 0).getDate();
}

/**
 * Gets the number of days in a year.
 * @param year - The year.
 * @returns The number of days in the year.
 */
function daysInYear(year: number): number {
  return (year % 4 === 0 && year % 100 > 0) || year % 400 == 0 ? 366 : 365;
}

/**
 * Normalizes a date to UTC by setting the time to 00:00:00.000.
 * @param date - The date to normalize.
 * @returns The normalized date in UTC.
 */
function utcNormalizedDate(date: string | Date | dayjs.Dayjs): dayjs.Dayjs {
  return dayjs(date).hour(0).minute(0).second(0).millisecond(0);
}

/**
 * Capitalizes the first letter of a string.
 * @param input - The input string.
 * @returns The string with the first letter capitalized.
 */
const capitalizeFirstLetter = (input: string): string => {
  return input?.charAt(0)?.toUpperCase() + input?.slice(1)?.toLowerCase();
};

/**
 * Converts a hexadecimal color code to RGB format.
 *
 * @param hex - The hexadecimal color code to convert.
 * @returns The RGB representation of the color code.
 */
const hexToRGB = (hex: string): string => {
  if (hex.startsWith("#")) hex = hex.slice(1);
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return r + ", " + g + ", " + b;
};

/**
 * Converts an RGB color string to a hexadecimal color string.
 * @param {string} rgbString - The RGB color string to convert.
 * @returns {string} - The hexadecimal color string.
 */
const RGBToHex = (rgbString, alpha = null): string => {
  const rgb = rgbString.match(/\d+/g);
  let str =
    "#" +
    (
      (1 << 24) +
      (parseInt(rgb[0]) << 16) +
      (parseInt(rgb[1]) << 8) +
      parseInt(rgb[2])
    )
      .toString(16)
      .slice(1);

  if (alpha != null && typeof alpha === "string") {
    str += alpha;
  } else if (alpha != null && typeof alpha === "number") {
    str += Math.round(alpha * 255).toString(16);
  }

  return str;
};

export const Format = {
  split,
  getListItemKey,
  remToPx,
  pxToRem,
  fCurrency,
  fPercent,
  fNumber,
  fShortenNumber,
  fPrecision,
  fData,
  getPrecision,
  fDate,
  fProjectDate,
  fLocalMonthYear,
  fLocalMonthDay,
  fLocalMonth,
  fLocalDay,
  fLocalDate,
  fLocalDateShort,
  fTime,
  fLocalTime,
  fDateTime,
  fDateTimeSuffix,
  fDateTimeShort,
  fToNow,
  fToNowOrDateTime,
  fLastSyncedAt,
  fTimeAgo,
  getLastDayOfMonth,
  daysInMonth,
  daysInYear,
  utcNormalizedDate,
  capitalizeFirstLetter,
  hexToRGB,
  RGBToHex,
};
